import React, { memo, useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import Paragraph from '../molecules/Paragraph';
import RowCenterButton from '../molecules/RowCenterButton';
import DefaultButton from '../molecules/DefaultButton';
import DialogConfirm from '../molecules/DialogConfirm';
import MypageReturn from '../molecules/MypageReturn';
import { status_code_list as reception_status } from '../Const';
import Popup from '../molecules/Popup';
import MaintenanceMessage from '../molecules/MaintenanceMessage';

interface Props {
  status: number;
  is_staff: boolean;
  is_verification: boolean; // 契約書確認画面にて全ての契約書を確認したか（true=確認した）
  reception_id: number;
  is_signed: boolean;
  handleDecline: () => void | boolean;
  handleExecution: () => void;
  is_checked_contract_document?: boolean;
  info_notice: string;
  is_active_decline_btn: boolean;
  is_signer: boolean;
}

// KDANKGM05010_契約書内容確認
// KDANKGM05040_農業資金契約書内容確認
// KDANKGM05050_近代化資金契約書内容確認
const FooterConfirmationOfContractContents: React.FC<Props> = ({
  status,
  is_staff,
  is_verification,
  reception_id,
  is_signed,
  handleDecline,
  handleExecution,
  is_checked_contract_document,
  info_notice,
  is_active_decline_btn,
  is_signer,
}) => {
  const [modal_decline_open, setModalDeclineOpen] = useState(false);

  // 「契約書を表示する」ボタンを出力する
  const outputContractButton = () => {
    if (status >= reception_status.borrowing_application_result_approval) {
      return (
        <div className="mt-5">
          <Paragraph label="電子契約書" is_paragraph={true} />
          <Paragraph
            label={
              is_signer
                ? '「契約書を表示する」を押して、契約書を表示し、内容をご確認下さい。'
                : '電子署名する契約書はありません。'
            }
            is_paragraph={false}
          />
          {is_signer && (
            <RowCenterButton>
              <DefaultButton
                label="契約書を表示する"
                strong={false}
                handleClick={handleClickContract}
                className="footer-confirmation-button"
              />
            </RowCenterButton>
          )}
        </div>
      );
    }
    return <></>;
  };

  // 「辞退する」「契約手続きに進む」ボタンを出力する
  const outputProcedureButton = () => {
    // ユーザーが署名済みである、またはステータスが契約取下げ連携待ちの場合は非表示
    if (is_signed || status === reception_status.contract_withdrawal_wait) {
      return <></>;
    }

    // メンテナンスメッセージがセットされているかを判定
    const has_info_notice: boolean = info_notice !== '';
    // メンテナンスメッセージに「本人限定郵便」を含むかを判定
    const has_personal_limited_mail: boolean = info_notice.includes('本人限定郵便');
    if (is_signer) {
      return (
        <>
          <Paragraph label="ご契約" is_paragraph={true} />
          {/* メンテナンスメッセージ設定時 */}
          {has_info_notice && <MaintenanceMessage message={info_notice} />}
          {/* メンテナンスメッセージ未設定時または文面に本人限定郵便を含むメンテナンスメッセージ設定時 */}
          {(!has_info_notice || has_personal_limited_mail) && (
            <Paragraph
              label="ご確認いただいたご契約内容でお手続きを進められる方は、「契約手続きに進む」ボタンを押下して下さい。"
              is_paragraph={false}
            />
          )}
          <Row className="flex-lg-row flex-md-reverse flex-sm-column-reverse flex-column-reverse">
            <Col xs={12} sm={12} md={12} lg={6} xl={6} className="row-left-button-danger">
              <div className="text-left sp-button-center">
                <a
                  className={`btn btn-xl btn-danger ${!is_active_decline_btn && 'disabled-bg'}`}
                  onClick={handleClickOpenDecline}
                >
                  辞退する
                </a>
              </div>
            </Col>
            <Col xs={12} sm={12} md={12} lg={6} xl={6} className="row-right-button-small">
              <div className="text-right sp-button-center">
                <a
                  className={`btn btn-xl btn-main-color sp-btn-height ml-auto ${(!is_checked_contract_document ||
                    (has_info_notice && !has_personal_limited_mail)) &&
                    'disabled-bg'}`}
                  onClick={handleClickContractProcedure}
                >
                  契約手続きに進む
                </a>
              </div>
            </Col>
          </Row>
        </>
      );
    }
  };

  // 「契約書を表示する」ボタンをクリックした時のアクション
  const handleClickContract = () => {
    // KWANKGM00170_契約書確認に遷移
    location.href = `/confirm_contract?r_id=${reception_id}`;
  };

  // 「辞退する」をクリックした時のアクション
  const handleClickOpenDecline = () => {
    setModalDeclineOpen(true);
  };

  // 「契約手続きに進む」をクリックした時のアクション
  const handleClickContractProcedure = () => {
    if (!is_checked_contract_document) {
      return;
    }
    handleExecution();
  };

  // 辞退する確認モーダルの「辞退する」ボタンをクリックしたときのアクション
  const handleClickDecline = async () => {
    const result = await handleDecline();

    // 正常終了か判定
    if (result) {
      // 正常終了の場合、契約署名完了画面に遷移する
      location.href = `${location.origin}${process.env.MIX_MOCK_URL}/applicant/contract_signature_result?r_id=${reception_id}`;
    }
  };

  return (
    <>
      {outputContractButton()}
      {outputProcedureButton()}
      <MypageReturn is_staff={is_staff} />
      <Popup
        title="契約辞退確認"
        text="契約の辞退となりますが、よろしいですか？"
        first_button={{
          label: '辞退する',
          variant: 'danger',
          handleClick: () => {
            handleClickDecline();
            setModalDeclineOpen(false);
          },
        }}
        second_button={{
          label: '戻る',
          variant: 'outline-success',
          handleClick: () => {
            setModalDeclineOpen(false);
          },
        }}
        toggleClose={() => setModalDeclineOpen(false)}
        toggleShow={modal_decline_open}
      />
    </>
  );
};

export default memo(FooterConfirmationOfContractContents);
